import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Moment from "moment";
import { MdRefresh } from "react-icons/md";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import Alert from "react-bootstrap/Alert";
import Badge from "react-bootstrap/Badge";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

import {
  fetchPawGroupMetrics,
  fetchWorkflowErrorMetrics,
  resetMetricsFetcherStatus,
} from "./DashboardMetrics.actions";
import { hasRoleIn } from "../../rbac/RbacHelper";
import PERMISSIONS from "../../rbac/Permissions.json";
import "./DashboardMetrics.css";

const Metrics = (props) => (
  <div className="text-muted">
    {props.showPawMetrics && (
      <>
        <strong>Switches to Approve</strong>
        <Row>
          <Col>Open:</Col>
          <Col>
            <Badge bg="info" pill>
              <span
                id="metrics-open-switch-count"
                className="dashboard-metrics-count"
              >
                {props.metrics.openInterventions?props.metrics.openInterventions:"N/A"}
              </span>
            </Badge>
          </Col>
        </Row>
        <Row className="tiny-top-spacer">
          <Col>Owned:</Col>
          <Col>
            <Badge bg="success" pill>
              <span
                id="metrics-owned-switch-count"
                className="dashboard-metrics-count"
              >
                {props.metrics.ownedInterventions?props.metrics.ownedInterventions:"N/A"}
              </span>
            </Badge>
          </Col>
        </Row>
        <div className="small-top-spacer">
          <strong>Responses to Confirm</strong>
        </div>
        <Row>
          <Col>Open:</Col>
          <Col>
            <Badge bg="info" pill>
              <span
                id="metrics-open-response-count"
                className="dashboard-metrics-count"
              >
                {props.metrics.openResponses?props.metrics.openResponses:"N/A"}
              </span>
            </Badge>
          </Col>
        </Row>
        <Row className="tiny-top-spacer">
          <Col>Owned:</Col>
          <Col>
            <Badge bg="success" pill>
              <span
                id="metrics-owned-response-count"
                className="dashboard-metrics-count"
              >
                {props.metrics.ownedResponses?props.metrics.ownedResponses:"N/A"}
              </span>
            </Badge>
          </Col>
        </Row>
      </>
    )}
    {props.showWorkflowErrors && (
      <>
        <div className="small-top-spacer">
          <strong>Workflow Errors</strong>
        </div>
        <Row>
          <Col>Count:</Col>
          <Col>
            <Badge bg="danger" pill>
              <span
                id="metrics-fax-error-count"
                className="dashboard-metrics-count"
              >
                {props.metrics.workflowErrorCount || props.metrics.workflowErrorCount === 0 ? props.metrics.workflowErrorCount : "N/A"}
              </span>
            </Badge>
          </Col>
        </Row>
      </>
    )}
    <Row>
      <Col>
        <span className="text-muted" style={{ fontSize: "0.8em" }}>
          Updated:{" "}
          <strong>
            {Moment(props.metrics.requestDateTime).format("MM/DD hh:mm:ss")}
          </strong>
        </span>{" "}
        &nbsp;
        <span onClick={props.fetchMetrics}>
          <OverlayTrigger
            placement="right"
            overlay={<Tooltip>Refresh Metrics</Tooltip>}
          >
            {({ ref, ...triggerHandler }) => (
              <MdRefresh {...triggerHandler} className="refresh-link text-muted" ref={ref} />
            )}
          </OverlayTrigger>
        </span>
      </Col>
    </Row>
  </div>
);

const DashboardMetrics = () => {
  const dispatch = useDispatch();
  const pawGroupMetrics = useSelector((state) => state.dashboardMetrics.pawGroupMetrics);
  const workflowErrorMetrics = useSelector((state) => state.dashboardMetrics.workflowErrorMetrics);
  const metricsFetcherStatus = useSelector(
    (state) => state.dashboardMetrics.metricsFetcherStatus
  );

  const showWorkflowErrors = hasRoleIn([
    PERMISSIONS.PERM_SCHEDULER_ACCESS,
    PERMISSIONS.PERM_BULK_ACTION_CRUD,
  ]);

  const hasPawPermissions = hasRoleIn([
    PERMISSIONS.PERM_PAW_GROUP_USER_MANAGE,
  ]);

  // Combine metrics for display
  const metrics = pawGroupMetrics ? {
    ...pawGroupMetrics,
    workflowErrorCount: workflowErrorMetrics?.faxErrorCount,
    requestDateTime: pawGroupMetrics.requestDateTime || workflowErrorMetrics?.requestDateTime
  } : workflowErrorMetrics ? {
    workflowErrorCount: workflowErrorMetrics.faxErrorCount,
    requestDateTime: workflowErrorMetrics.requestDateTime
  } : null;

  useEffect(() => {
    if (hasPawPermissions) {
      dispatch(fetchPawGroupMetrics());
    }
    if (showWorkflowErrors) {
      dispatch(fetchWorkflowErrorMetrics());
    }
  }, [dispatch, showWorkflowErrors, hasPawPermissions]);

  useEffect(() => {
    if (metricsFetcherStatus === "success") {
      dispatch(resetMetricsFetcherStatus());
    }
  }, [metricsFetcherStatus, dispatch]);

  const handleFetchMetrics = () => {
    if (hasPawPermissions) {
      dispatch(fetchPawGroupMetrics());
    }
    if (showWorkflowErrors) {
      dispatch(fetchWorkflowErrorMetrics());
    }
  };

  return (
    <div>
      <Alert
        variant={metricsFetcherStatus === "failure" ? "danger" : "secondary"}
        className={metricsFetcherStatus !== "failure" ? "custom-alert" : ""}
      >
        {metricsFetcherStatus === "requested" ? (
          <p>Fetching metrics ...</p>
        ) : metricsFetcherStatus === "success" ||
          (metricsFetcherStatus === "idle" && metrics) ? (
          <Metrics metrics={metrics} fetchMetrics={handleFetchMetrics} showWorkflowErrors={showWorkflowErrors} showPawMetrics={hasPawPermissions} />
        ) : (
          <p>Failed to fetch metrics ...</p>
        )}
      </Alert>
    </div>
  );
};

export default DashboardMetrics;

